import { Routes, Route, Navigate } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import ChurchAdminRoute from './components/ChurchAdminRoute'
import { SuperAdminLayout } from './components/admin/SuperAdminLayout'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminChurchesPage from './pages/admin/AdminChurchesPage'
import AdminEventsPage from './pages/admin/AdminEventsPage'
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage'
import AdminScrapePage from './pages/admin/AdminScrapePage'
import AdminReviewQueuePage from './pages/admin/AdminReviewQueuePage'
import AdminWaitlistPage from './pages/admin/AdminWaitlistPage'
import AdminChurchRequestsPage from './pages/admin/AdminChurchRequestsPage'
import AdminCoverageMapPage from './pages/admin/AdminCoverageMapPage'
import AdminDiscoverPage from './pages/admin/AdminDiscoverPage'
import { useAuthStore } from './store/auth'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import OnboardingPage from './pages/OnboardingPage'
import SearchPage from './pages/SearchPage'
import EventDetailPage from './pages/EventDetailPage'
import ChurchProfilePage from './pages/ChurchProfilePage'
import ChurchDashboardPage from './pages/ChurchDashboardPage'
import CreateEventPage from './pages/CreateEventPage'
import EditChurchEventPage from './pages/EditChurchEventPage'
import BillingPage from './pages/BillingPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import ChurchEditPage from './pages/ChurchEditPage'
import HostManagementPage from './pages/HostManagementPage'
import DayOfPage from './pages/DayOfPage'
import BulletinPage from './pages/BulletinPage'
import MyEventsPage from './pages/MyEventsPage'
import ProfilePage from './pages/ProfilePage'
import ChurchesPage from './pages/ChurchesPage'
import HostEntryPage from './pages/HostEntryPage'
import CreatePersonEventPage from './pages/CreatePersonEventPage'
import PersonEventManagePage from './pages/PersonEventManagePage'
import EditPersonEventPage from './pages/EditPersonEventPage'
import ComingSoonPage from './pages/ComingSoonPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import ClaimChurchPage from './pages/ClaimChurchPage'
import NewChurchPage from './pages/NewChurchPage'
import ForChurchesPage from './pages/ForChurchesPage'
import ChurchWelcomePage from './pages/ChurchWelcomePage'
import ChurchOnboardingPage from './pages/ChurchOnboardingPage'
import ChurchEventsPage from './pages/ChurchEventsPage'
import MembersPage from './pages/MembersPage'
import ChurchCalendarPage from './pages/ChurchCalendarPage'
import GuestRsvpManagePage from './pages/GuestRsvpManagePage'
import { PageViewTracker } from './components/PageViewTracker'

function SuperAdminRoute() {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'super_admin') return <Navigate to="/" replace />
  return <SuperAdminLayout />
}

function App() {
  return (
    <>
      <PageViewTracker />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/app" element={<Navigate to="/" replace />} />
      <Route path="/waitlist" element={<ComingSoonPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/churches" element={<ChurchesPage />} />
      <Route path="/host" element={<HostEntryPage />} />
      <Route path="/invites/:token" element={<AcceptInvitePage />} />
      <Route path="/events/:id" element={<EventDetailPage />} />
      <Route path="/churches/:slug" element={<ChurchProfilePage />} />
      <Route path="/churches/:id/claim" element={<ClaimChurchPage />} />
      <Route path="/for-churches" element={<ForChurchesPage />} />
      <Route path="/welcome/:slug" element={<ChurchWelcomePage />} />
      <Route path="/rsvps/manage/:token" element={<GuestRsvpManagePage />} />

      {/* Authenticated routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/churches/new" element={<NewChurchPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/church-onboarding/:slug" element={<ChurchOnboardingPage />} />
        <Route path="/events/new" element={<CreatePersonEventPage />} />
        <Route path="/my-events" element={<MyEventsPage />} />
        <Route path="/events/:id/occurrences/:occurrenceId/manage" element={<PersonEventManagePage />} />
        <Route path="/events/:id/edit" element={<EditPersonEventPage />} />
        <Route path="/notifications" element={<Navigate to="/profile" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      {/* Super admin routes */}
      <Route element={<SuperAdminRoute />}>
        <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/churches" element={<AdminChurchesPage />} />
        <Route path="/admin/events" element={<AdminEventsPage />} />
        <Route path="/admin/waitlist" element={<AdminWaitlistPage />} />
        <Route path="/admin/church-requests" element={<AdminChurchRequestsPage />} />
        <Route path="/admin/scrape" element={<AdminScrapePage />} />
        <Route path="/admin/coverage" element={<AdminCoverageMapPage />} />
        <Route path="/admin/discover" element={<AdminDiscoverPage />} />
        <Route path="/admin/review" element={<AdminReviewQueuePage />} />
        <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
      </Route>

      {/* Church admin routes */}
      <Route element={<ChurchAdminRoute />}>
        <Route path="/churches/:slug/dashboard" element={<ChurchDashboardPage />} />
        <Route path="/churches/:slug/events/new" element={<CreateEventPage />} />
        <Route path="/churches/:slug/events/:id/edit" element={<EditChurchEventPage />} />
        <Route path="/churches/:slug/billing" element={<BillingPage />} />
        <Route path="/churches/:slug/edit" element={<ChurchEditPage />} />
        <Route path="/churches/:slug/occurrences/:occurrenceId/host" element={<HostManagementPage />} />
        <Route path="/churches/:slug/occurrences/:occurrenceId/day-of" element={<DayOfPage />} />
        <Route path="/churches/:slug/events" element={<ChurchEventsPage />} />
        <Route path="/churches/:slug/members" element={<MembersPage />} />
        <Route path="/churches/:slug/calendar" element={<ChurchCalendarPage />} />
        <Route path="/churches/:slug/bulletin" element={<BulletinPage />} />
      </Route>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
    </Routes>
    </>
  )
}

export default App
